import React, { useEffect, useState } from 'react';
import { onlyDigit } from 'app/constants/regex';
import { CurrencyDataCalc } from 'app/constants/types';
import { useTranslation } from 'react-i18next';

interface Props {
  bank: CurrencyDataCalc[];
  cbar: CurrencyDataCalc[];
}

const useCurrencyBox = (props: Props) => {
  const { bank, cbar } = props;
  const { t } = useTranslation();

  const [bankType, setBankType] = useState({ value: 'bd', label: t('homePage.internalBankRate') });

  const calculateOptions = [
    { value: 'bd', label: t('homePage.internalBankRate') },
    { value: 'cb', label: t('homePage.cbar') },
  ];

  const [fromCurrency, setFromCurrency] = useState<{
    value: string;
    label: JSX.Element;
    buy: string;
    sell: string;
    text: string;
  }>();

  const [toCurrency, setToCurrency] = useState<{
    value: string;
    label: JSX.Element;
    buy: string;
    sell: string;
    text: string;
  }>();

  const [valueFrom, setValueFrom] = useState('');
  const [valueTo, setValueTo] = useState('');

  useEffect(() => {
    const aznBD = bank.find((item) => item.value === 'AZN');
    const usdBD = bank.find((item) => item.value === 'USD');

    const aznCB = cbar.find((item) => item.value === 'AZN');
    const usdCB = cbar.find((item) => item.value === 'USD');

    const fromCurrencyBank = bank.find((item) => item.value === fromCurrency?.value);
    const toCurrencyBank = bank.find((item) => item.value === toCurrency?.value);
    const fromCurrencyCbar = cbar.find((item) => item.value === fromCurrency?.value);
    const toCurrencyCbar = cbar.find((item) => item.value === toCurrency?.value);

    if (bankType.value === 'bd') {
      if (fromCurrencyBank === undefined && toCurrencyBank === undefined) {
        setFromCurrency({ ...aznBD, label: <img src={aznBD.label} alt="flags" width="24" height="16" /> });
        setToCurrency({ ...usdBD, label: <img src={usdBD.label} alt="flags" width="24" height="16" /> });
        handleChangeFrom(valueFrom, aznBD.sell, usdBD?.buy);
      } else if (fromCurrencyBank === undefined && toCurrencyBank !== undefined) {
        setFromCurrency({ ...aznBD, label: <img src={aznBD.label} alt="flags" width="24" height="16" /> });
        setToCurrency({
          ...toCurrencyBank,
          label: <img src={toCurrencyBank?.label} alt="flags" width="24" height="16" />,
        });
        handleChangeFrom(valueFrom, aznBD.sell, toCurrencyBank?.buy);
      } else if (fromCurrencyBank !== undefined && toCurrencyBank === undefined) {
        setFromCurrency({
          ...fromCurrencyBank,
          label: <img src={fromCurrencyBank?.label} alt="flags" width="24" height="16" />,
        });
        setToCurrency({ ...usdBD, label: <img src={usdBD.label} alt="flags" width="24" height="16" /> });
        handleChangeFrom(valueFrom, fromCurrencyBank?.sell, usdBD?.buy);
      } else if (fromCurrencyBank?.value === toCurrencyBank?.value) {
        handleChangeFrom(valueFrom, fromCurrencyBank?.sell, toCurrencyBank?.sell);
      } else {
        setFromCurrency({
          ...fromCurrencyBank,
          label: <img src={fromCurrencyBank?.label} alt="flags" width="24" height="16" />,
        });
        setToCurrency({
          ...toCurrencyBank,
          label: <img src={toCurrencyBank?.label} alt="flags" width="24" height="16" />,
        });
        handleChangeFrom(valueFrom, fromCurrencyBank?.sell, toCurrencyBank?.buy);
      }
    }

    if (bankType.value === 'cb') {
      if (fromCurrencyCbar === undefined && toCurrencyCbar === undefined) {
        setFromCurrency({ ...aznCB, label: <img src={aznCB.label} alt="flags" width="24" height="16" /> });
        setToCurrency({ ...usdCB, label: <img src={usdCB.label} alt="flags" width="24" height="16" /> });
        handleChangeFrom(valueFrom, aznCB.sell, usdCB?.buy);
      } else if (fromCurrencyCbar === undefined && toCurrencyCbar !== undefined) {
        setFromCurrency({ ...aznCB, label: <img src={aznCB.label} alt="flags" width="24" height="16" /> });
        setToCurrency({
          ...toCurrencyCbar,
          label: <img src={toCurrencyCbar?.label} alt="flags" width="24" height="16" />,
        });
        handleChangeFrom(valueFrom, aznCB.sell, toCurrencyCbar?.buy);
      } else if (fromCurrencyCbar !== undefined && toCurrencyCbar === undefined) {
        setFromCurrency({
          ...fromCurrencyCbar,
          label: <img src={fromCurrencyCbar?.label} alt="flags" width="24" height="16" />,
        });
        setToCurrency({ ...usdCB, label: <img src={usdCB.label} alt="flags" width="24" height="16" /> });
        handleChangeFrom(valueFrom, fromCurrencyCbar?.sell, usdCB?.buy);
      } else {
        setFromCurrency({
          ...fromCurrencyCbar,
          label: <img src={fromCurrencyCbar?.label} alt="flags" width="24" height="16" />,
        });
        setToCurrency({
          ...toCurrencyCbar,
          label: <img src={toCurrencyCbar?.label} alt="flags" width="24" height="16" />,
        });
        handleChangeFrom(valueFrom, fromCurrencyCbar?.sell, toCurrencyCbar?.buy);
      }
    }
  }, [bankType]);

  const onChangeBankType = (newValue: { value: string; label: string }) => {
    setBankType({ value: newValue.value, label: newValue.label });
    handleChangeFrom(valueFrom, fromCurrency?.sell, toCurrency?.buy);
  };

  const handleChangeFrom = (e: any, from: any, to: any) => {
    let amount: string;

    if (e === '' || onlyDigit.test(e)) {
      amount = e;

      let newAmount: number;

      if (from === undefined && to === undefined) {
        newAmount = (+amount * +fromCurrency?.sell) / +toCurrency?.buy;
      } else if (from !== undefined && to === undefined) {
        newAmount = (+amount * +from) / +toCurrency?.buy;
      } else if (from === undefined && to !== undefined) {
        newAmount = (+amount * +fromCurrency?.sell) / +to;
      } else {
        newAmount = (+amount * +from) / +to;
      }

      setValueFrom(amount);

      if (amount === undefined || amount.length === 0) {
        setValueTo('');
      } else {
        setValueTo(newAmount.toFixed(2).toString());
      }
    }
  };

  const handleChangeFromCurrency = (newValue: any) => {
    setFromCurrency({ ...newValue, label: <img src={newValue.label} alt="flags" width="24" height="16" /> });

    if (newValue.value === toCurrency.value) {
      handleChangeFrom(valueFrom, 1, 1);
    } else {
      handleChangeFrom(valueFrom, newValue.sell, undefined);
    }
  };

  const handleChangeTo = (e: any) => {
    let amount: string;

    if (e === '' || onlyDigit.test(e)) {
      amount = e;

      let newAmount = (+amount * +toCurrency.sell) / +fromCurrency.buy;

      if (fromCurrency?.value === toCurrency?.value) {
        newAmount = +amount;
      }

      setValueTo(amount);

      if (amount === undefined || amount.length === 0) {
        setValueFrom('');
      } else {
        setValueFrom(newAmount.toFixed(2).toString());
      }
    }
  };

  const handleChangeToCurrency = (newValue: any) => {
    setToCurrency({ ...newValue, label: <img src={newValue.label} alt="flags" width="24" height="16" /> });
    if (newValue.value === fromCurrency.value) {
      handleChangeFrom(valueFrom, 1, 1);
    } else {
      handleChangeFrom(valueFrom, undefined, newValue.buy);
    }
  };

  const handleSwitchCurrency = () => {
    setFromCurrency(toCurrency);
    setToCurrency(fromCurrency);
    handleChangeFrom(valueFrom, toCurrency?.sell, fromCurrency?.buy);
  };

  return {
    t,
    calculateOptions,
    fromCurrency,
    toCurrency,
    bankType,
    valueFrom,
    valueTo,
    handleChangeFrom,
    handleChangeTo,
    onChangeBankType,
    handleChangeFromCurrency,
    handleChangeToCurrency,
    handleSwitchCurrency,
  };
};

export default useCurrencyBox;
